/**
 * API hook for Oura Ring integration
 */
import { useCallback } from "react";
import { API_BASE } from "../config";
import { useAuth } from "../auth/AuthProvider";
import { reportPaymentRequired } from "../lib/api-client";

interface InviteResponse {
  invite_url: string;
  invite_token: string;
  expires_at: number;
}

interface StatusResponse {
  connected: boolean;
  status: string;
  connected_at: number | null;
}

export function useOuraApi() {
  const { ensureToken } = useAuth();

  const createInvite = useCallback(
    async (patientId: string): Promise<InviteResponse> => {
      const token = await ensureToken();
      const resp = await fetch(`${API_BASE}/oura/invite`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ patient_id: patientId }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        reportPaymentRequired(resp.status, data); // Oura invites are Pro-gated
        throw new Error(data.error || `HTTP ${resp.status}`);
      }
      return data;
    },
    [ensureToken]
  );

  const getStatus = useCallback(
    async (patientId: string): Promise<StatusResponse> => {
      const token = await ensureToken();
      const resp = await fetch(
        `${API_BASE}/oura/status/${encodeURIComponent(patientId)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || `HTTP ${resp.status}`);
      return data;
    },
    [ensureToken]
  );

  const disconnect = useCallback(
    async (patientId: string): Promise<void> => {
      const token = await ensureToken();
      const resp = await fetch(
        `${API_BASE}/oura/disconnect/${encodeURIComponent(patientId)}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!resp.ok) {
        const data = await resp.json();
        throw new Error(data.error || `HTTP ${resp.status}`);
      }
    },
    [ensureToken]
  );

  return { createInvite, getStatus, disconnect };
}
